import { useState, useEffect } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import contactImage from '../../../assets/images/spa-retro-logo-removebg.png';
import { trackEvent } from '../../../data/services/analytics';

function ContactPage() {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    issueType: '',
    message: '',
    website: '' // honeypot — real users never fill this in
  });
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaValue) {
      setSubmitStatus({ type: 'error', message: 'Please complete the reCAPTCHA' });
      return;
    }
    setIsSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_API_BASE || '';
      const res = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          captchaToken: captchaValue,
          expectedAction: 'contact_submit' // optional label
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'verify failed');
      if (!formData.website) {
        trackEvent('generate_lead', { form_name: 'contact', issue_type: formData.issueType || 'unspecified' });
      }
      setSubmitStatus({ type: 'success', message: 'Thank you! Your message has been sent successfully.' });
      setFormData({ name: '', email: '', issueType: '', message: '', website: '' });
      setCaptchaValue(null);
    } catch (err) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <section className="contact-page">
      <div className="container">
        <h1 className="page-title">Get In Touch</h1>
        <p className="page-subtitle">
          Have a question, found a bug, or need assistance? We're here to help!
        </p>

        <div className="contact-content">
          <div className="contact-form-container">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="issueType">How can we help? *</label>
                <select
                  id="issueType"
                  name="issueType"
                  value={formData.issueType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select an option</option>
                  <option value="technical">Technical Issue</option>
                  <option value="bug">Report a Bug</option>
                  <option value="feature">Feature Request</option>
                  <option value="account">Account Support</option>
                  <option value="billing">Billing Question</option>
                  <option value="predictions">Predictions/Analytics Question</option>
                  <option value="data">Data Accuracy Concern</option>
                  <option value="feedback">General Feedback</option>
                  <option value="partnership">Partnership Inquiry</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Please provide as much detail as possible..."
                ></textarea>
              </div>

              <div className="form-group honeypot-field">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
              </div>

              <div className="form-group captcha-group">
                <ReCAPTCHA
                  sitekey={process.env.SANDLOT_APP_RECAPTCHA_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  theme="light"
                />
              </div>

              {submitStatus && (
                <div className={`submit-message ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>

            <div className="contact-info">
              <p>Or reach us directly at:</p>
              <a href="mailto:support@sandlotpicks.com" className="email-link">
                support@sandlotpicks.com
              </a>
            </div>
          </div>

          <div className="contact-image-container">
            <img 
              src={contactImage} 
              alt="Sandlot Picks Analytics" 
              className="contact-image"
            />
            <div className="image-overlay-text">
              <h3>We're Here to Help</h3>
              <p>Our team typically responds within 24 hours</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactPage;
